import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';
import { useEnum, ENUM_NAMES } from '../hooks/useEnum';

interface Reminder {
  id: number;
  pet_id: number;
  client_id: number;
  pet_name: string;
  owner_name: string;
  reminder_type: string;
  title: string;
  description: string;
  due_date: string;
  status: string;
  sent_at: string;
  created_at: string;
}

interface Pet {
  id: number;
  name: string;
  client_id: number;
}

interface Stats {
  summary: {
    due_this_week: number;
    overdue: number;
    due_today: number;
    sent: number;
    cancelled: number;
    completed: number;
    total_count: number;
  };
  by_type: { reminder_type: string; count: number }[];
}

const RemindersPage = () => {
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [pets, setPets] = useState<Pet[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [editReminder, setEditReminder] = useState<Reminder | null>(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', due_date: '', status: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState<Reminder | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const { values: reminderTypes, getDisplayName: getReminderTypeName } = useEnum(ENUM_NAMES.REMINDER_TYPES);
  const { values: reminderStatuses } = useEnum(ENUM_NAMES.REMINDER_STATUSES);
  const [formData, setFormData] = useState({
    pet_id: '',
    client_id: '',
    reminder_type: 'medication',
    title: '',
    description: '',
    due_date: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [remindersRes, petsRes, statsRes] = await Promise.all([
        api.get<{ data: Reminder[] }>(API_ENDPOINTS.REMINDERS.LIST),
        api.get<{ data: Pet[] }>(API_ENDPOINTS.PATIENTS.LIST),
        api.get<Stats>(API_ENDPOINTS.REMINDERS.LIST),
      ]);
      setReminders(remindersRes.data ?? []);
      setPets(petsRes.data ?? []);
      setStats(statsRes);
    } catch (err) {
      setError('Failed to load reminders');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await api.post(API_ENDPOINTS.REMINDERS.CREATE, {
        ...formData,
        pet_id: parseInt(formData.pet_id),
        client_id: parseInt(formData.client_id),
      });
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      setError('Failed to create reminder');
      console.error(err);
    }
  };

  const handleSend = async (id: number) => {
    try {
      await api.post(API_ENDPOINTS.REMINDERS.SEND(id));
      fetchData();
    } catch (err) {
      setError('Failed to send reminder');
      console.error(err);
    }
  };

  const handleCancel = async () => {
    if (!cancelConfirm) return;
    setCancelling(true);
    try {
      await api.delete(API_ENDPOINTS.REMINDERS.DELETE(cancelConfirm.id));
      setCancelConfirm(null);
      fetchData();
    } catch {
      setError('Failed to cancel reminder');
    } finally { setCancelling(false); }
  };

  const openEdit = (r: Reminder) => {
    setEditReminder(r);
    setEditForm({ title: r.title, description: r.description || '', due_date: r.due_date || '', status: r.status });
  };

  const handleEdit = async () => {
    if (!editReminder) return;
    setEditSaving(true);
    try {
      await api.put(API_ENDPOINTS.REMINDERS.UPDATE(editReminder.id), {
        title: editForm.title, message: editForm.description,
        scheduled_at: editForm.due_date, status: editForm.status,
      });
      setEditReminder(null);
      fetchData();
    } catch { setError('Failed to update reminder'); } finally { setEditSaving(false); }
  };

  const resetForm = () => {
    setFormData({ pet_id: '', client_id: '', reminder_type: 'medication', title: '', description: '', due_date: '' });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'sent': return 'info';
      case 'completed': return 'success';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const handlePetChange = (petId: string) => {
    const pet = pets.find(p => p.id === parseInt(petId));
    setFormData({
      ...formData,
      pet_id: petId,
      client_id: pet?.client_id?.toString() || '',
    });
  };

  const filteredReminders = reminders.filter(r => {
    if (filterStatus && r.status !== filterStatus) return false;
    if (filterType && r.reminder_type !== filterType) return false;
    return true;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Reminders</Typography>
      
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary">Due This Week</Typography>
              <Typography variant="h4">{stats.summary.due_this_week}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary">Overdue</Typography>
              <Typography variant="h4" color="error">{stats.summary.overdue}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary">Due Today</Typography>
              <Typography variant="h4">{stats.summary.due_today}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary">Sent</Typography>
              <Typography variant="h4">{stats.summary.sent}</Typography>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          New Reminder
        </Button>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {reminderStatuses.map(s => <MenuItem key={s.code} value={s.code}>{s.display_name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Type</InputLabel>
          <Select value={filterType} label="Type" onChange={e => setFilterType(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {reminderTypes.map(t => <MenuItem key={t.code} value={t.code}>{t.display_name}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Pet</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredReminders.map(reminder => (
              <TableRow key={reminder.id}>
                <TableCell>{reminder.pet_name}</TableCell>
                <TableCell>{reminder.owner_name}</TableCell>
                <TableCell>{getReminderTypeName(reminder.reminder_type)}</TableCell>
                <TableCell>{reminder.title}</TableCell>
                <TableCell>{reminder.description || '-'}</TableCell>
                <TableCell>{reminder.due_date}</TableCell>
                <TableCell>
                  <Chip label={reminder.status} color={getStatusColor(reminder.status) as any} size="small" />
                </TableCell>
                <TableCell>
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(reminder)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  {reminder.status === 'pending' && (
                    <>
                      <Button size="small" onClick={() => handleSend(reminder.id)}>Send</Button>
                      <Tooltip title="Cancel"><IconButton size="small" color="error" onClick={() => setCancelConfirm(reminder)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {filteredReminders.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center">No reminders found</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Reminder</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Pet</InputLabel>
            <Select value={formData.pet_id} label="Pet" onChange={e => handlePetChange(e.target.value)}>
              {pets.map(pet => <MenuItem key={pet.id} value={pet.id}>{pet.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Type</InputLabel>
            <Select value={formData.reminder_type} label="Type" onChange={e => setFormData({ ...formData, reminder_type: e.target.value })}>
            {reminderTypes.map(t => <MenuItem key={t.code} value={t.code}>{t.display_name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth margin="normal" label="Title" value={formData.title}
            onChange={e => setFormData({ ...formData, title: e.target.value })} />
          <TextField fullWidth margin="normal" label="Due Date" type="date" value={formData.due_date}
            onChange={e => setFormData({ ...formData, due_date: e.target.value })}
            InputLabelProps={{ shrink: true }} />
          <TextField fullWidth margin="normal" label="Description" multiline rows={3} value={formData.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>
      {/* Edit dialog */}
      <Dialog open={!!editReminder} onClose={() => setEditReminder(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Reminder</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="normal" label="Title" value={editForm.title}
            onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} />
          <TextField fullWidth margin="normal" label="Due Date" type="date" InputLabelProps={{ shrink: true }}
            value={editForm.due_date} onChange={e => setEditForm(f => ({ ...f, due_date: e.target.value }))} />
          <TextField fullWidth margin="normal" label="Description" multiline rows={2}
            value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select value={editForm.status} label="Status" onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
            {reminderStatuses.map(s => <MenuItem key={s.code} value={s.code}>{s.display_name}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditReminder(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit} disabled={editSaving}>{editSaving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel confirmation */}
      <Dialog open={!!cancelConfirm} onClose={() => setCancelConfirm(null)}>
        <DialogTitle>Cancel Reminder</DialogTitle>
        <DialogContent>
          <Typography>Cancel reminder "<strong>{cancelConfirm?.title}</strong>" for <strong>{cancelConfirm?.pet_name}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirm(null)}>Keep</Button>
          <Button variant="contained" color="error" onClick={handleCancel} disabled={cancelling}>{cancelling ? 'Cancelling...' : 'Cancel Reminder'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RemindersPage;
